import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import moment, { Moment } from "moment";

import { usePrevious } from "utils/hooks";
import { memoize } from "lodash";
import {
  PredefinedPeriod,
  defaultPeriod,
  findByKey,
  findByLabel,
  findMaxForRetention,
} from "utils/dateRange";
import { useLocation, useNavigate } from "react-router-dom";
import { retention as retentionLimits } from "utils/limits";
import { useLimitedRetention } from "utils/routes";

export type DateRange = {
  from: Moment;
  to: Moment;
  label?: string;
};

export type DateRangeUpdate =
  | {
      from: Moment;
      to: Moment;
    }
  | {
      label: string;
    }
  | undefined;

export type SetDateRange = (rangeUpdate: DateRangeUpdate) => void;

const RetentionContext = React.createContext(retentionLimits.default);

export function WithRetention({
  retention,
  children,
}: {
  retention: moment.Duration;
  children: React.ReactNode;
}) {
  const actualRetention = useLimitedRetention(retention);

  return (
    <RetentionContext.Provider value={actualRetention}>
      {children}
    </RetentionContext.Provider>
  );
}

function useRetention() {
  return useContext(RetentionContext);
}

export const DateRangeContext = React.createContext<DateRange>({
  from: moment(),
  to: moment(),
});
const failFastUpdateHandler = () => {
  throw new Error("must be used in a provider");
};
const DateRangeSetContext = React.createContext<SetDateRange>(
  failFastUpdateHandler,
);

export function useDateRange() {
  return useContext(DateRangeContext);
}

export function useSetDateRange() {
  return useContext(DateRangeSetContext);
}

const DateRangeIsClippedContext = React.createContext<boolean>(false);

export function useDateRangeIsClipped() {
  return useContext(DateRangeIsClippedContext);
}

function invalidRange(reason: string): DateRange {
  console.error("could not parse date range:", reason);
  const to = moment();
  const from = to.clone().subtract(24, "hours");
  return {
    from,
    to,
    label: "invalid date range",
  };
}

const encodeRange = (opts: DateRangeUpdate): string | undefined => {
  if (!opts) {
    return undefined;
  }
  if (!("label" in opts)) {
    const { from, to } = opts;
    return `${from.unix()}-${to.unix()}`;
  }

  const { label } = opts;
  const period = findByLabel(label);
  if (!period) {
    return undefined;
  }

  return period.key;
};

function periodToRange(period: PredefinedPeriod) {
  const [from, to] = period.currentRange();
  return {
    label: period.label,
    from,
    to,
  };
}

const decodeRange = (encoded: string | undefined | null): DateRange => {
  if (!encoded) {
    return periodToRange(defaultPeriod);
  }
  const period = findByKey(encoded);
  if (period) {
    return periodToRange(period);
  }

  const explicitRange = encoded.split("-");
  if (explicitRange.length !== 2) {
    return invalidRange("expected two parts");
  }
  const [fromStr, toStr] = explicitRange;
  const fromUnix = parseInt(fromStr, 10);
  const toUnix = parseInt(toStr, 10);
  if (isNaN(fromUnix) || isNaN(toUnix)) {
    return invalidRange("expected numbers");
  }
  return {
    from: moment.unix(fromUnix),
    to: moment.unix(toUnix),
  };
};

const DATE_RANGE_SLACK_MS = 1000 * 60 * 5;

function useCachedValue<T>(resolve: () => T, cacheMs: number): T {
  const ref = useRef<T>(resolve());
  const lastRefreshAt = useRef(Date.now());

  if (lastRefreshAt.current < Date.now() - cacheMs) {
    ref.current = resolve();
    lastRefreshAt.current = Date.now();
  }

  useEffect(() => {
    ref.current = resolve();
    lastRefreshAt.current = Date.now();
  }, [resolve]);

  return ref.current;
}

function useDecodedRange(encodedRange: string | undefined | null) {
  // We memoize the decodeRange function for some stability for symbolic ranges
  // like 'last 24h', but expire the cache after a set amount of time in order
  // for the resolved definition not to get too stale.
  //
  // Note that we do *not* cause the component to re-render when the cache
  // expires: we intentionally only refresh the resolved value of a symbolic
  // range when the page is re-rendered.

  const decodeRangeMemoized = useCallback(() => {
    return memoize(decodeRange);
  }, []);
  const decode = useCachedValue(decodeRangeMemoized, DATE_RANGE_SLACK_MS);

  const range = useMemo(() => {
    return decode(encodedRange);
  }, [decode, encodedRange]);

  return range;
}

export function useRetentionHorizon() {
  const retention = useRetention();

  const resolveHorizon = useCallback(() => {
    // We subtract twice because multi-day subtraction is better handled in the original
    // format (a duration of days) to keep the same time of day across DST changes:
    // https://github.com/moment/moment/issues/78#issuecomment-2979637
    return moment()
      .subtract(retention)
      .subtract(DATE_RANGE_SLACK_MS, "milliseconds");
  }, [retention]);
  const horizon = useCachedValue(resolveHorizon, DATE_RANGE_SLACK_MS);

  return horizon;
}

function clampRange(range: DateRange, retention: moment.Duration) {
  if ("label" in range) {
    return findMaxForRetention(retention);
  }
  const now = moment();
  const cutOff = now.clone().subtract(retention);
  if (range.to.isBefore(cutOff)) {
    // not a lot of options
    return defaultPeriod;
  }

  const origFrom = range.from.valueOf();
  const newFrom = cutOff.valueOf();

  return {
    from: moment(Math.max(origFrom, newFrom)),
    to: range.to,
  };
}

export function WithDateRange({ children }: { children: React.ReactNode }) {
  const retention = useRetention();
  const prevRetention = usePrevious(retention);

  const loc = useLocation();
  const navigate = useNavigate();

  const rawSearch = loc.search;
  const params = new URLSearchParams(rawSearch);
  const rawRange = params.get("t");
  const range = useDecodedRange(rawRange);
  const [savedRange, setSavedRange] = useState<string | undefined | null>(
    undefined,
  );

  const retentionWidened =
    prevRetention && prevRetention.valueOf() < retention.valueOf();
  const earliestAvailable = useRetentionHorizon();
  const rangeClippedByRetention = range.from.isBefore(earliestAvailable);

  // If we don't have room within this retention period to use the full range,
  // save the original range and navigate to a similar range within the
  // retention window
  useEffect(() => {
    if (!rangeClippedByRetention) {
      return;
    }
    setSavedRange(rawRange);
    const clampedRange = clampRange(range, retention);
    const t = encodeRange(clampedRange);
    const searchParams = new URLSearchParams(window.location.search);
    if (t) {
      searchParams.set("t", t);
    } else {
      searchParams.delete("t");
    }

    navigate(
      {
        search: searchParams.toString(),
      },
      {
        replace: true,
      },
    );
  }, [navigate, rangeClippedByRetention, range, retention, rawRange]);

  // Restore the saved range when navigating to a section with wider retention
  useEffect(() => {
    if (!retentionWidened || !savedRange) {
      return;
    }
    setSavedRange(undefined);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("t", savedRange);
    navigate(
      {
        search: searchParams.toString(),
      },
      {
        replace: true,
      },
    );
  }, [navigate, savedRange, retentionWidened]);

  const setRange = useCallback(
    (opts: DateRangeUpdate) => {
      const encoded = encodeRange(opts);
      const searchParams = new URLSearchParams(window.location.search);
      if (encoded) {
        searchParams.set("t", encoded);
      } else {
        searchParams.delete("t");
      }
      navigate({
        search: searchParams.toString(),
      });
    },
    [navigate],
  );

  return (
    <DateRangeSetContext.Provider value={setRange}>
      <DateRangeContext.Provider value={range}>
        <DateRangeIsClippedContext.Provider value={savedRange != undefined}>
          {children}
        </DateRangeIsClippedContext.Provider>
      </DateRangeContext.Provider>
    </DateRangeSetContext.Provider>
  );
}
